import { memo, useCallback } from 'react';

import { Tooltip } from '../../Tooltip';
import { Button as CustomButton } from '../../Button';
import { useFilesService } from '../../services/hooks';
import { Box } from '../../Box';
import { Counter } from '../../Counter';
import { Paper } from '../../Paper';
import { FileInputValue } from '../FileInput';
import { FilePreview } from '../../FilePreview';
import { Icon } from '../../Icon';
import { Typography } from '../../Typography';
import { EllipsisText } from '../../EllipsisText';

import useStyles from './MultipleFileInput.styles';

/**
 * Свойства компонента {@link MultipleFileInputPreview}
 */
interface MultipleFileInputPreviewProps {
  /**
   * Значение файла для отображения.
   */
  file: FileInputValue;

  /**
   * Индекс файла в списке.
   */
  index: number;

  /**
   * Функция для удаления документа.
   */
  removeDocument: () => void;

  /**
   * Флаг, указывающий, отключен ли компонент.
   */
  disabled: boolean;

  /**
   * Флаг, указывающий, использовать ли авторизацию при получении файла.
   */
  withAuth: boolean;

  /**
   * Показывать счётчик с порядковым номером.
   *
   * @defaultValue `true`
   */
  withCounter?: boolean;

  /**
   * Тестовый идентификатор родительского компонента.
   */
  testId: string;
}

/**
 * Компонент MultipleFileInputPreview представляет собой превью для одного файла в списке загруженных файлов.
 * Он показывает миниатюру файла с возможностью просмотра и удаления файла.
 *
 * @public
 * @param file - Значение файла для отображения.
 * @param index - Индекс файла в списке.
 * @param removeDocument - Функция для удаления документа.
 * @param disabled - Флаг, указывающий, отключен ли компонент.
 * @param withAuth - Флаг, указывающий, использовать ли авторизацию при получении файла.
 * @param testId - Тестовый идентификатор родительского компонента.
 * @returns  Компонент MultipleFileInputPreview.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link MultipleFileInputPreviewProps}
 */
export const MultipleFileInputPreview = memo(function MultipleFileInputPreview({
  file,
  index,
  removeDocument,
  disabled,
  withAuth,
  withCounter = true,
  testId,
}: MultipleFileInputPreviewProps) {
  const { classes } = useStyles();
  const filesService = useFilesService();

  /**
   * Функция для загрузки файла.
   * Если источником является строка, предполагается, что это ссылка на файл, который нужно загрузить.
   * Если источником является объект File, он возвращается напрямую.
   *
   * @param source - Источник файла (строка с URL или объект File).
   * @returns  Загруженный файл в виде Blob.
   */
  const load: () => Promise<Blob> = useCallback(async () => {
    if (typeof file.source === 'string') {
      const fileData = await filesService.getFile(file.source, withAuth);
      const loadedFile = await fileData.file;

      return loadedFile;
    } else {
      return file.source;
    }
  }, [file.source, filesService, withAuth]);

  return (
    <Box
      position="relative"
      width={{ xs: 92, sm: 140 }}
      height={{ xs: 92, sm: 140 }}
      testId={`${testId}-preview-${index}`}
    >
      {withCounter && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="absolute"
          top={10}
          left={10}
          radius="round"
          height={20}
          width={20}
          zIndex={1}
        >
          <Counter backgroundColor="gray" counter={index + 1} />
        </Box>
      )}
      {!disabled && (
        <Box position="absolute" top={7} right={8} zIndex={1}>
          <CustomButton
            size="extraSmall"
            variant="quaternaryAccent"
            onClick={removeDocument}
            testId={`${testId}-remove-${index}`}
            icon={
              <Icon name="cancel" size="16" color="original.brand.primary" />
            }
          />
        </Box>
      )}
      <Tooltip
        title={
          (typeof file.source !== 'string' && file.source.name) ||
          'Без названия'
        }
        placement="bottom"
      >
        <Box
          position="absolute"
          bottom={1}
          right={1}
          zIndex={1}
          left={1}
          bgcolor="bg.greyscale.primary"
          py={{ xs: '2', md: '4' }}
          px="12"
          borderBottomLeftRadius="l"
          borderBottomRightRadius="l"
        >
          <EllipsisText rows={1}>
            <Typography variant="caption">
              {(typeof file.source !== 'string' && file.source.name) ||
                'Без названия'}
            </Typography>
          </EllipsisText>
        </Box>
      </Tooltip>
      <Paper className={classes.filePreviewPaper}>
        <FilePreview onLoad={load} clickable />
      </Paper>
    </Box>
  );
});
